import { toast } from 'vue3-toastify';

import logoDark from '@/assets/images/logo-dark.png';
import logo from '@/assets/images/logo.png';
import type { IAgencyNotificationSettings } from '@/interfaces/agency/settings/agency/notification-settings';
import type { IAgencyProfile } from '@/interfaces/agency/settings/agency/profile';
import type { IUserProfile } from '@/interfaces/agency/settings/user-management/users';
import authService from '@/services/agency/auth';
import agencyServices from '@/services/agency/settings/agency/profiles';
import userManagementServices from '@/services/agency/settings/user-management';
import { useAuthStore } from '@/stores/agency/auth';

export const useAgencyStore = defineStore('agencyStore', {
  state: () => ({
    logo: logo as string | null,
    logoDark: logoDark as string | null,
    agencyProfile: {} as IAgencyProfile,
    refAgencyProfile: {} as IAgencyProfile,
    isAgencyProfileEditing: false,
    agencyNotificationSettings: [] as IAgencyNotificationSettings[],
    authorisedPersonProfile: null as IUserProfile | null
  }),
  getters: {
    getAgencyProfile: state => {
      return state.agencyProfile;
    },
    getNotificationSettings: state => {
      return state.agencyNotificationSettings;
    }
  },
  actions: {
    async fetchAgencyProfile() {
      const { data } = await agencyServices.getAgencyProfile();

      const { timeInterval, timeFormat, dateFormat, timeZone } = data;

      this.agencyProfile = data;
      this.refAgencyProfile = JSON.parse(JSON.stringify(data));

      localStorage.setItem('agencyProfile', JSON.stringify(data));

      localStorage.setItem(
        'timeFormat',
        JSON.stringify({
          h24: timeFormat === '24',
          timeInterval: timeInterval || 15
        })
      );

      // TODO: Refactor date format mapping in Backend
      const luxonDateFormat = (dateFormat || 'DD-MM-YYYY')
        .replace('DD', 'dd')
        .replace('MM', 'MM')
        .replace('YYYY', 'yyyy');

      localStorage.setItem('dateFormat', luxonDateFormat);

      localStorage.setItem('timeZone', timeZone || 'UTC');
    },

    async resetAgencyProfile() {
      this.agencyProfile = JSON.parse(JSON.stringify(this.refAgencyProfile));
    },

    async updateAgencyProfile(payload: Partial<IAgencyProfile>) {
      await agencyServices.updateAgencyProfile(payload);

      toast.success('Agency profile updated successfully');
      await this.fetchAgencyProfile();
      await this.refreshAuthTenantData();

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },

    async updateAgencyDateTime(payload: Partial<IAgencyProfile>) {
      await agencyServices.updateAgencyProfile(payload);

      toast.success('Agency date, time, and currency updated successfully');
      await this.fetchAgencyProfile();
      await this.refreshAuthTenantData();

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },

    async refreshAuthTenantData() {
      const authStore = useAuthStore();
      const { id: userId } = authStore.getUserCDetails;

      await Promise.all([authStore.fetchLoggedInUserProfile(), authStore.fetchUserTenantRoles(userId)]);
    },

    async setAgencyProfileEditing(value: boolean) {
      this.isAgencyProfileEditing = value;
    },

    async updateAgencyProfileLogos(file: File, field: string) {
      await agencyServices.updateAgencyProfileLogos(file, field);

      this.fetchAgencyProfile();
      toast.success('Agency profile updated successfully');
    },

    async fetchAuthorisedPersonDetails() {
      const { data } = await userManagementServices.fetchUsers({ roleId: [1], page: 1, limit: 1 });

      this.authorisedPersonProfile = (data?.data?.[0] as IUserProfile) ?? null;
    },

    async fetchAgencyNotificationSettings() {
      const { data } = await authService.fetchAgencyNotificationSettings();

      this.agencyNotificationSettings = data;
    },

    async updateAgencyNotificationSettings(payload: IAgencyNotificationSettings) {
      await authService.updateAgencyNotificationSettings(payload);

      this.fetchAgencyNotificationSettings();
      toast.success('Notification setting updated successfully!');
    }
  }
});
